import React, { useId, useState } from 'react';
import Block from './Block';
import {
  ChartContainer, ChartLegend, ChartLegendItem, ChartTooltipContent, useContainerWidth,
} from '../../components/ui/chart';
import { cssZoom } from '../../utils/zoom';

const fmt = (v) => {
  if (v == null || Number.isNaN(v)) return '—';
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${(v / 1_000).toFixed(1)}k`;
  if (abs >= 10) return v.toFixed(1);
  return v.toFixed(2);
};

const dateText = (p) => (p?.date ? String(p.date).slice(0, 10) : '');

// Round-number y ticks covering [min, max].
function niceTicks(min, max, count = 4) {
  const span = max - min || Math.abs(max) || 1;
  const raw = span / count;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw) || raw;
  const lo = Math.floor(min / step) * step;
  const hi = Math.ceil(max / step) * step;
  const ticks = [];
  for (let t = lo; t <= hi + step * 1e-6; t += step) ticks.push(Number(t.toFixed(10)));
  return ticks;
}

/**
 * History (solid, soft area fill) + forecast (dashed, interval band) in the
 * shadcn chart style: horizontal grid only, no axis lines, a hover cursor with
 * a tooltip card. `height` is fixed; the width tracks the container so the
 * viewBox matches it 1:1. `showAxis={false}` renders a bare sparkline.
 */
export function TSForecastChart({ historical = [], forecast = [], width = 700, height = 240, showAxis = true }) {
  const [containerRef, measuredWidth] = useContainerWidth(width);
  const [hover, setHover] = useState(null);
  const gradId = `ts-fill-${useId().replace(/:/g, '')}`;
  const w = Math.max(measuredWidth, 80);

  const all = [
    ...historical.map((p) => p.value),
    ...forecast.map((p) => p.value),
    ...forecast.map((p) => p.ci_low),
    ...forecast.map((p) => p.ci_high),
  ].filter((v) => v != null && Number.isFinite(v));

  if (all.length === 0) return null;

  const padL = showAxis ? 50 : 2;
  const padR = showAxis ? 8 : 2;
  const padT = showAxis ? 26 : 4;
  const padB = showAxis ? 30 : 4;
  const W = w - padL - padR;
  const H = height - padT - padB;

  const minV = Math.min(...all);
  const maxV = Math.max(...all);
  const yTicks = showAxis ? niceTicks(minV, maxV) : [];
  const range = maxV - minV;
  const flatPad = Math.abs(maxV) * 0.1 || 1;
  const domMin = showAxis ? Math.min(yTicks[0], minV) : (range ? minV - range * 0.08 : minV - flatPad);
  const domMax = showAxis ? Math.max(yTicks[yTicks.length - 1], maxV) : (range ? maxV + range * 0.08 : maxV + flatPad);
  const domRange = domMax - domMin || 1;
  const yScale = (v) => H - ((v - domMin) / domRange) * H;

  const allPoints = [...historical, ...forecast];
  const n = allPoints.length;
  const xScale = (i) => (n > 1 ? (i / (n - 1)) * W : W / 2);

  const histIdx = historical.length;
  const lastHist = historical[histIdx - 1];
  const splitX = histIdx > 0 ? xScale(histIdx - 1) : 0;

  const pt = (i, v) => `${xScale(i).toFixed(1)},${yScale(v).toFixed(1)}`;
  const histLine = historical.map((p, i) => `${i === 0 ? 'M' : 'L'}${pt(i, p.value)}`).join(' ');
  const histArea = historical.length > 1
    ? `${histLine} L${xScale(histIdx - 1).toFixed(1)},${H} L0,${H} Z`
    : '';
  // Forecast starts at the last actual so the dashed segment joins the line.
  const forecastLine = forecast.length > 0
    ? [
      lastHist ? `M${pt(histIdx - 1, lastHist.value)}` : `M${pt(histIdx, forecast[0].value)}`,
      ...forecast.map((p, i) => `L${pt(histIdx + i, p.value)}`),
    ].join(' ')
    : '';
  const hasCI = forecast.some((p) => p.ci_low != null && p.ci_high != null);
  const ciPoly = (forecast.length > 0 && hasCI) ? [
    lastHist ? `M${pt(histIdx - 1, lastHist.value)}` : '',
    ...forecast.map((p, i) => `${!lastHist && i === 0 ? 'M' : 'L'}${pt(histIdx + i, p.ci_high)}`),
    ...forecast.slice().reverse().map((p, i) => `L${pt(histIdx + forecast.length - 1 - i, p.ci_low)}`),
    'Z',
  ].join(' ') : '';

  // X labels: evenly spaced dates that never crowd each other.
  const maxLabels = Math.max(2, Math.min(6, Math.floor(W / 96)));
  const xLabelIdx = showAxis
    ? Array.from(new Set(Array.from({ length: maxLabels }, (_, k) => Math.round((k * (n - 1)) / (maxLabels - 1)))))
    : [];

  const onMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / cssZoom() - padL;
    const i = Math.max(0, Math.min(n - 1, Math.round((x / W) * (n - 1))));
    setHover(i);
  };

  const hp = hover != null ? allPoints[hover] : null;
  const hoverIsForecast = hover != null && hover >= histIdx;
  const tooltipItems = hp ? [
    {
      name: hoverIsForecast ? 'Forecast' : 'Actual',
      value: fmt(hp.value),
      color: 'var(--chart-1)',
      shape: hoverIsForecast ? 'dashed' : 'line',
    },
    ...(hoverIsForecast && hp.ci_low != null && hp.ci_high != null
      ? [{ name: '90% interval', value: `${fmt(hp.ci_low)}–${fmt(hp.ci_high)}`, color: 'var(--chart-band)' }]
      : []),
  ] : [];

  return (
    <ChartContainer ref={containerRef}>
      <svg
        width={w}
        height={height}
        viewBox={`0 0 ${w} ${height}`}
        style={{ display: 'block', overflow: 'visible' }}
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
      >
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--chart-1)" stopOpacity={showAxis ? 0.16 : 0.1} />
            <stop offset="100%" stopColor="var(--chart-1)" stopOpacity={0} />
          </linearGradient>
        </defs>
        <g transform={`translate(${padL},${padT})`}>
          {yTicks.map((v) => (
            <g key={v}>
              <line x1={0} y1={yScale(v)} x2={W} y2={yScale(v)} stroke="var(--border)" strokeWidth={1} />
              <text x={-10} y={yScale(v)} textAnchor="end" dominantBaseline="middle" fill="var(--muted-foreground)" fontSize={12.5}>
                {fmt(v)}
              </text>
            </g>
          ))}

          {showAxis && forecast.length > 0 && histIdx > 0 && (
            <g>
              <rect x={splitX} y={showAxis ? -padT + 4 : 0} width={Math.max(W - splitX, 0)} height={H + (showAxis ? padT - 4 : 0)}
                fill="var(--muted)" opacity={showAxis ? 0.55 : 0.6} rx={showAxis ? 6 : 3} />
              {showAxis && (
                <text x={splitX + 10} y={-padT + 19} fill="var(--muted-foreground)" fontSize={12.5} fontWeight={500}>
                  Forecast
                </text>
              )}
            </g>
          )}

          {histArea && showAxis && <path d={histArea} fill={`url(#${gradId})`} stroke="none" />}
          {ciPoly && <path d={ciPoly} fill="var(--chart-band)" stroke="none" />}
          {histLine && (
            <path d={histLine} fill="none" stroke="var(--chart-1)" strokeWidth={showAxis ? 2 : 1.5}
              strokeLinejoin="round" strokeLinecap="round" />
          )}
          {forecastLine && (
            <path d={forecastLine} fill="none" stroke="var(--chart-1)" strokeWidth={showAxis ? 2 : 1.5}
              strokeDasharray="5 4" strokeLinejoin="round" strokeLinecap="round" />
          )}

          {xLabelIdx.map((i, k) => (
            <text
              key={i}
              x={xScale(i)}
              y={H + 18}
              textAnchor={k === 0 ? 'start' : k === xLabelIdx.length - 1 ? 'end' : 'middle'}
              fill="var(--muted-foreground)"
              fontSize={12.5}
            >
              {dateText(allPoints[i]) || String(i)}
            </text>
          ))}

          {hp && (
            <g pointerEvents="none">
              <line x1={xScale(hover)} y1={0} x2={xScale(hover)} y2={H} stroke="var(--muted-foreground)" strokeOpacity={0.4} strokeWidth={1} />
              <circle cx={xScale(hover)} cy={yScale(hp.value)} r={showAxis ? 4.5 : 3} fill="var(--chart-1)" stroke="var(--card)" strokeWidth={2} />
            </g>
          )}
          <rect x={0} y={0} width={W} height={H} fill="transparent" />
        </g>
      </svg>
      {hp && (
        <ChartTooltipContent
          x={padL + xScale(hover)}
          y={padT + yScale(hp.value) - 20}
          containerWidth={w}
          label={dateText(hp)}
          items={tooltipItems}
        />
      )}
      {showAxis && (
        <ChartLegend style={{ marginTop: 10 }}>
          <ChartLegendItem color="var(--chart-1)" shape="line">Actual</ChartLegendItem>
          {forecast.length > 0 && <ChartLegendItem color="var(--chart-1)" shape="dashed">Forecast</ChartLegendItem>}
          {hasCI && <ChartLegendItem color="var(--chart-band)">90% interval</ChartLegendItem>}
        </ChartLegend>
      )}
    </ChartContainer>
  );
}

const TimeSeriesForecastBlock = ({ blockData }) => {
  const {
    target_column: targetColumn = 'value',
    historical = [],
    forecast = [],
    horizon = 0,
  } = blockData || {};

  const lastForecast = forecast[forecast.length - 1];

  return (
    <Block
      icon="trending-up"
      title={`Forecast: ${targetColumn.replace(/_/g, ' ')}`}
      description={`${historical.length} historical points · ${horizon} step${horizon !== 1 ? 's' : ''} ahead`}
    >
      <TSForecastChart historical={historical} forecast={forecast} />
      {lastForecast && (
        <div className="q-stats" style={{ marginTop: 16 }}>
          <div className="q-stat">
            <span className="q-stat__label">Forecast at {dateText(lastForecast)}</span>
            <span className="q-stat__value">{fmt(lastForecast.value)}</span>
            {lastForecast.ci_low != null && lastForecast.ci_high != null && (
              <span className="q-stat__hint">90% interval {fmt(lastForecast.ci_low)}–{fmt(lastForecast.ci_high)}</span>
            )}
          </div>
        </div>
      )}
    </Block>
  );
};

export default TimeSeriesForecastBlock;
